import { useRef, useState } from 'react'
import { useTranslations } from 'use-intl'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCompany } from '@/contexts/CompanyContext'
import { previewSieFile, importSieFile, uploadSieDocument, SieImportResult, SieFilePreview } from '@/clients/sieImportApiClient'
import DropZone from '@/components/upload/DropZone'
import UploadProgress, { FileUploadStatus } from '@/components/upload/UploadProgress'
import { userFacingErrorMessage } from '@/utils/userFacingError';

type Props = {
  open: boolean
  onClose: () => void
  onImported: () => void
}

type FileStatus =
  | { kind: 'pending' }
  | { kind: 'previewing' }
  | { kind: 'preview'; preview: SieFilePreview; confirmedAccountingMethod: 'accrual' | 'cash' }
  | { kind: 'preview_error'; message: string }
  | { kind: 'importing' }
  | { kind: 'done'; result: SieImportResult }
  | { kind: 'import_error'; message: string }

type FileEntry = {
  file: File
  status: FileStatus
}

type Phase = 'idle' | 'previewing' | 'preview' | 'importing' | 'done'

type DocEntry = {
  localKey: string
  name: string
  status: 'uploading' | 'success' | 'error'
  id?: string
  error?: string
}

function formatDate(d: string): string {
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`
}

function totalVerifications(entries: FileEntry[]): number {
  return entries.reduce((sum, e) => {
    return sum + (e.status.kind === 'preview' ? e.status.preview.verificationCount : 0)
  }, 0)
}

export function SieImportDialog({ open, onClose, onImported }: Props) {
  const t = useTranslations('consultantOnboarding.admin.sie')
  const tc = useTranslations('common.actions')
  const { company } = useCompany()
  const inputRef = useRef<HTMLInputElement>(null)
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [phase, setPhase] = useState<Phase>('idle')
  const [docEntries, setDocEntries] = useState<DocEntry[]>([])

  const uploadedDocumentIds = docEntries.filter(e => e.status === 'success').map(e => e.id!)
  const isDocUploading = docEntries.some(e => e.status === 'uploading')
  const docProgressFiles: FileUploadStatus[] = docEntries.map(e => ({
    name: e.name,
    status: e.status === 'success' ? 'success' : e.status === 'error' ? 'error' : 'uploading',
    error: e.error,
  }))

  function updateEntry(index: number, status: FileStatus) {
    setEntries(prev => prev.map((e, i) => i === index ? { ...e, status } : e))
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    setEntries(files.map(file => ({ file, status: { kind: 'pending' } })))
    setPhase('idle')
    setDocEntries([])
  }

  async function handleDocumentFilesSelected(files: File[]) {
    if (!company) return
    const timestamp = Date.now()
    const newEntries: DocEntry[] = files.map((f, i) => ({
      localKey: `${timestamp}-${i}`,
      name: f.name,
      status: 'uploading',
    }))
    setDocEntries(prev => [...prev, ...newEntries])
    await Promise.all(files.map(async (file, i) => {
      const localKey = newEntries[i].localKey
      try {
        const id = await uploadSieDocument(company.id, file)
        setDocEntries(prev => prev.map(e => e.localKey === localKey ? { ...e, status: 'success', id } : e))
      } catch (err) {
        const error = userFacingErrorMessage(err, t('uploadFailed'))
        setDocEntries(prev => prev.map(e => e.localKey === localKey ? { ...e, status: 'error', error } : e))
      }
    }))
  }

  async function handlePreviewAll() {
    if (entries.length === 0) return
    setPhase('previewing')
    // Preview all files in parallel
    await Promise.all(entries.map(async (entry, i) => {
      updateEntry(i, { kind: 'previewing' })
      try {
        const preview = await previewSieFile(entry.file, company?.id)
        // Defensive fallback (should not normally happen — the backend always
        // resolves a value when companyId + a primary fiscal year are present):
        // never default to accrual, matching AC3/AC4's safe-default rule.
        const confirmedAccountingMethod = preview.accountingMethod?.resolvedAccountingMethod ?? 'cash'
        updateEntry(i, { kind: 'preview', preview, confirmedAccountingMethod })
      } catch (err) {
        updateEntry(i, { kind: 'preview_error', message: userFacingErrorMessage(err, t('previewFailed')) })
      }
    }))
    setPhase('preview')
  }

  async function handleImportAll() {
    if (!company) return
    setPhase('importing')
    // Import files sequentially to avoid fiscal year conflicts
    for (let i = 0; i < entries.length; i++) {
      const status = entries[i].status
      if (status.kind !== 'preview') continue
      const { confirmedAccountingMethod } = status
      updateEntry(i, { kind: 'importing' })
      try {
        const result = await importSieFile(company.id, entries[i].file, confirmedAccountingMethod, uploadedDocumentIds)
        updateEntry(i, { kind: 'done', result })
        onImported()
      } catch (err) {
        updateEntry(i, { kind: 'import_error', message: userFacingErrorMessage(err, t('importFailed')) })
      }
    }
    setPhase('done')
  }

  function handleClose() {
    setEntries([])
    setPhase('idle')
    setDocEntries([])
    if (inputRef.current) inputRef.current.value = ''
    onClose()
  }

  const busy = phase === 'previewing' || phase === 'importing'
  const previewable = entries.length > 0 && (phase === 'idle' || phase === 'preview')
  const allPreviewed = entries.length > 0 && entries.every(e => e.status.kind === 'preview' || e.status.kind === 'preview_error')
  const hasAnyPreview = entries.some(e => e.status.kind === 'preview')
  const isCurrentYear = entries.some(
    e => e.status.kind === 'preview' && e.status.preview.isCurrentYear
  )
  const hasConflict = entries.some(
    e => e.status.kind === 'preview' && !!e.status.preview.conflictError
  )
  const canImport = hasAnyPreview && !hasConflict && !isDocUploading && (!isCurrentYear || uploadedDocumentIds.length > 0)
  let importBlockedTitle: string | undefined
  if (isDocUploading) importBlockedTitle = t('waitForUploads')
  else if (!canImport) importBlockedTitle = t('needDocuments')

  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2 pr-1">
          {/* File picker */}
          <div
            className={`border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-surface-inset/60 transition-colors ${busy ? 'pointer-events-none opacity-60' : ''}`}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".se,.si,.sie"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            {entries.length > 0 ? (
              <div className="space-y-1">
                {entries.map((e, i) => (
                  <p key={i} className="text-sm font-medium">{e.file.name}</p>
                ))}
                {!busy && (
                  <p className="text-xs text-muted-foreground mt-2">{t('change')}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('pick')}
              </p>
            )}
          </div>

          {/* Per-file preview cards */}
          {entries.length > 0 && phase !== 'idle' && (
            <div className="space-y-3">
              {entries.map((entry, i) => (
                <FileCard
                  key={i}
                  entry={entry}
                  onAccountingMethodChange={(method) => {
                    if (entry.status.kind !== 'preview') return
                    updateEntry(i, { ...entry.status, confirmedAccountingMethod: method })
                  }}
                />
              ))}
            </div>
          )}

          {/* Document upload gate — shown when preview covers the current fiscal year and no conflicts */}
          {phase === 'preview' && allPreviewed && isCurrentYear && !hasConflict && (
            <div className="space-y-3 rounded-lg border border-warning/40 bg-warning-weak p-4">
              <p className="text-sm font-medium text-warning-foreground">
                {t('gateTitle')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('gateBody')}
              </p>
              <DropZone
                onFilesSelected={handleDocumentFilesSelected}
                disabled={busy}
              />
              {docProgressFiles.length > 0 && (
                <UploadProgress files={docProgressFiles} />
              )}
            </div>
          )}

          {/* Attached documents summary — shown after import completes */}
          {phase === 'done' && docEntries.filter(e => e.status === 'success').length > 0 && (
            <div className="rounded-lg border border-border p-3 text-sm">
              <p className="text-xs font-medium text-muted-foreground mb-1">{t('attached')}</p>
              <ul className="space-y-0.5">
                {docEntries.filter(e => e.status === 'success').map(e => (
                  <li key={e.localKey} className="text-xs truncate">{e.name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={handleClose} disabled={busy}>
            {phase === 'done' ? t('close') : tc('cancel')}
          </Button>

          {phase === 'previewing' && (
            <Button disabled>{t('analysing')}</Button>
          )}

          {(phase === 'idle' || (phase === 'preview' && !allPreviewed)) && entries.length > 0 && !busy && (
            <Button onClick={handlePreviewAll}>
              {t('analyse', { count: entries.length })}
            </Button>
          )}

          {phase === 'preview' && allPreviewed && hasAnyPreview && !busy && (
            <Button
              onClick={handleImportAll}
              disabled={!canImport}
              title={importBlockedTitle}
            >
              {t('import', { count: String(totalVerifications(entries)) })}
              {entries.length > 1 && ` ${t('importFiles', { count: String(entries.filter(e => e.status.kind === 'preview').length) })}`}
            </Button>
          )}

          {phase === 'importing' && (
            <Button disabled>{t('importing')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type FileCardProps = {
  entry: FileEntry
  onAccountingMethodChange: (method: 'accrual' | 'cash') => void
}

// The message may be a list (a refused file names every problem on its own line), hence pre-line.
function FileErrorCard({ fileName, message }: { fileName: string; message: string }) {
  return (
    <div className="rounded-lg border border-danger/30 bg-danger-weak p-3 text-sm">
      <p className="font-medium">{fileName}</p>
      <p className="text-xs text-danger mt-1 whitespace-pre-line">{message}</p>
    </div>
  )
}

function FileCard({ entry, onAccountingMethodChange }: FileCardProps) {
  const t = useTranslations('consultantOnboarding.admin.sie')
  const { status } = entry

  if (status.kind === 'pending' || status.kind === 'previewing') {
    return (
      <div className="rounded-lg border border-border p-3 text-sm">
        <p className="font-medium text-muted-foreground">{entry.file.name}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {status.kind === 'previewing' ? t('analysing') : t('pending')}
        </p>
      </div>
    )
  }

  if (status.kind === 'importing') {
    return (
      <div className="rounded-lg border border-border p-3 text-sm opacity-60">
        <p className="font-medium">{entry.file.name}</p>
        <p className="text-xs text-muted-foreground mt-1">{t('importing')}</p>
      </div>
    )
  }

  if (status.kind === 'preview_error' || status.kind === 'import_error') {
    return <FileErrorCard fileName={entry.file.name} message={status.message} />
  }

  if (status.kind === 'done') {
    const { result } = status
    return (
      <div className={`rounded-lg border p-3 text-sm ${result.status === 'completed' ? 'border-success/30 bg-success-weak' : 'border-warning/30 bg-warning-weak'}`}>
        <p className="font-medium">{entry.file.name}</p>
        <p className="text-xs mt-1">{t('imported', { count: result.importedCount })}{result.errorCount > 0 ? t('failedSuffix', { count: String(result.errorCount) }) : ''}</p>
      </div>
    )
  }

  // preview
  const { preview, confirmedAccountingMethod } = status
  const accountingMethod = preview.accountingMethod
  return (
    <div className={`rounded-lg border p-3 text-sm space-y-2 ${preview.conflictError ? 'border-danger/30 bg-danger-weak' : 'border-border'}`}>
      <p className="font-medium">{entry.file.name}</p>
      {preview.conflictError && (
        <p className="text-xs text-danger">{preview.conflictError}</p>
      )}

      {/* Accounting method — given prominent placement, not buried below file details:
          a wrong value here has real tax/bookkeeping consequences. */}
      <div className="rounded-md border border-border-strong bg-surface-inset/60 p-2 space-y-1.5">
        {accountingMethod?.source === 'existing-fiscal-year' ? (
          <>
            <span className="text-xs font-medium">
              {t(`methods.${confirmedAccountingMethod}`)}
            </span>
            <p className="text-xs text-muted-foreground">
              {t(`reasons.${accountingMethod.reasonCode}`)}
            </p>
          </>
        ) : (
          <>
            <Select
              value={confirmedAccountingMethod}
              onValueChange={(v) => onAccountingMethodChange(v as 'accrual' | 'cash')}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accrual">{t('methods.accrual')}</SelectItem>
                <SelectItem value="cash">{t('methods.cash')}</SelectItem>
              </SelectContent>
            </Select>
            {accountingMethod && (
              <p className="text-xs text-muted-foreground">
                {t(`reasons.${accountingMethod.reasonCode}`)}
              </p>
            )}
          </>
        )}
        {accountingMethod?.disagreementWarning && (
          <p className="text-xs text-warning-foreground">
            {t('disagreement')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <span className="text-muted-foreground">{t('company')}</span>
        <span>{preview.companyName ?? '—'}</span>
        <span className="text-muted-foreground">{t('chart')}</span>
        <span className="font-mono">{preview.chartType ?? '—'}</span>
        <span className="text-muted-foreground">{t('verifications')}</span>
        <span>{preview.verificationCount}</span>
      </div>
      {preview.fiscalYears.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {preview.fiscalYears.map(fy => (
            <span key={fy.yearId} className="rounded bg-surface-inset px-2 py-0.5 text-xs font-mono">
              {formatDate(fy.startDate)} – {formatDate(fy.endDate)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
